import {
  CHART_MESSAGE_TYPES,
  HOST_MESSAGE_TYPES,
  createEnvelope,
  isTrustedMessageEvent
} from "./embed-protocol.js";

/**
 * @param {string} parentOrigin
 * @param {Record<string, (payload:Record<string, unknown>) => void>} handlers
 */
export function createIframeMessenger(parentOrigin, handlers) {
  if (!isExactHttpOrigin(parentOrigin)) {
    throw new Error("A valid exact parent origin is required for iframe messaging.");
  }

  function post(type, payload = {}, requestId = null) {
    if (window.parent === window || !CHART_MESSAGE_TYPES.has(type)) return;
    window.parent.postMessage(createEnvelope(type, payload, requestId), parentOrigin);
  }

  function receive(event) {
    if (!isTrustedMessageEvent(event, parentOrigin, window.parent, HOST_MESSAGE_TYPES)) return;
    const handler = handlers[event.data.type];
    if (typeof handler === "function") handler(event.data.payload);
  }

  window.addEventListener("message", receive);

  return {
    post,
    ready: (payload = {}) => post("chart.ready", payload),
    symbolChanged: (symbol, symbolId = null) => post(
      "chart.symbolChanged",
      symbolId ? { symbol, symbolId } : { symbol }
    ),
    timeframeChanged: (timeframe) => post("chart.timeframeChanged", { timeframe }),
    themeChanged: (theme) => post("chart.themeChanged", { theme }),
    resized: (width, height) => post("chart.resize", { width, height }),
    performance: (payload) => post("chart.performance", payload),
    error: (code, message, recoverable = true) => post("chart.error", { code, message, recoverable }),
    dispose() {
      window.removeEventListener("message", receive);
    }
  };
}

function isExactHttpOrigin(value) {
  try {
    const url = new URL(value);
    return (url.protocol === "http:" || url.protocol === "https:") && url.origin === value;
  } catch {
    return false;
  }
}
